import type {
  Agent,
  AgentVersion,
  PatchAgentBody,
  UpsertAgentBody,
} from '../types/agent.js';

/**
 * Per-request options accepted by every {@link IAgentsService} method. Kept as a
 * dedicated type (rather than a bare `signal` parameter) so the request contract
 * can grow — e.g. custom headers, an idempotency key, or a timeout — without
 * touching each method signature again.
 */
export interface AgentsRequestOptions {
  /** Abort signal forwarded to `fetch`, so callers can cancel in-flight requests. */
  signal?: AbortSignal;
}

export interface IAgentsService {
  list(workspaceName: string, options?: AgentsRequestOptions): Promise<Agent[]>;
  get(
    workspaceName: string,
    name: string,
    options?: AgentsRequestOptions,
  ): Promise<Agent>;
  create(
    workspaceName: string,
    name: string,
    body: UpsertAgentBody,
    options?: AgentsRequestOptions,
  ): Promise<Agent>;
  replace(
    workspaceName: string,
    name: string,
    body: UpsertAgentBody,
    options?: AgentsRequestOptions,
  ): Promise<Agent>;
  patch(
    workspaceName: string,
    name: string,
    body: PatchAgentBody,
    options?: AgentsRequestOptions,
  ): Promise<Agent>;
  delete(
    workspaceName: string,
    name: string,
    options?: AgentsRequestOptions,
  ): Promise<void>;
  snapshot(
    workspaceName: string,
    name: string,
    options?: AgentsRequestOptions,
  ): Promise<AgentVersion>;
  listVersions(
    workspaceName: string,
    name: string,
    options?: AgentsRequestOptions,
  ): Promise<AgentVersion[]>;
  getVersion(
    workspaceName: string,
    name: string,
    version: number,
    options?: AgentsRequestOptions,
  ): Promise<AgentVersion>;
}

export class AgentNotFoundError extends Error {
  readonly code = 'AgentNotFound';
  constructor(workspaceName: string, name: string) {
    super(`Agent '${name}' was not found in workspace '${workspaceName}'.`);
  }
}

export class AgentAlreadyExistsError extends Error {
  readonly code = 'ResourceAlreadyExists';
  constructor(workspaceName: string, name: string) {
    super(
      `An agent named '${name}' already exists in workspace '${workspaceName}'.`,
    );
  }
}
